import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class GoogleMapsLoaderService {
  private loadedSubject = new Subject<boolean>();
  private isLoaded = false;
  private isLoading = false;

  constructor() { }

  /**
   * Load Google Maps API script dynamically
   * @returns Observable<boolean> - emits true when Google Maps is loaded
   */
  loadGoogleMaps(): Observable<boolean> {
    // If already loaded, return immediately
    if (this.isLoaded) {
      return of(true);
    }

    // If currently loading, return the existing observable
    if (this.isLoading) {
      return this.loadedSubject.asObservable();
    }

    // Start loading
    this.isLoading = true;

    // Check if Google Maps is already loaded (in case it was loaded elsewhere)
    if (typeof google !== 'undefined' && google.maps) {
      this.isLoaded = true;
      this.isLoading = false;
      this.loadedSubject.next(true);
      return of(true);
    }

    // Create script element
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.defer = true;
    script.src = `${environment.googleMapUrl}${environment.googleMapKey}`;

    // Handle script load success
    script.onload = () => {
      this.isLoaded = true;
      this.isLoading = false;
      this.loadedSubject.next(true);
    };

    // Handle script load error
    script.onerror = () => {
      this.isLoading = false;
      this.loadedSubject.error('Failed to load Google Maps API');
    };

    // Append script to document head
    document.head.appendChild(script);

    return this.loadedSubject.asObservable();
  }
}